// Utility to sync asset <-> data type links
import { getDataTypeById, updateDataType } from '@/services/dataRetentionService';
import { getAssets } from '@/services/assetsService';

/**
 * Syncs the asset ID list on each data type entity when an asset adds/removes a data type
 * @param assetId - The asset being updated
 * @param prevDataTypeIds - Array of data type IDs before the change
 * @param newDataTypeIds - Array of data type IDs after the change
 * @returns Promise<void>
 */
export async function syncDataTypeAssets(
  assetId: string,
  prevDataTypeIds: string[],
  newDataTypeIds: string[]
) {
  // Find added and removed data type IDs
  const added = newDataTypeIds.filter(x => !prevDataTypeIds.includes(x));
  const removed = prevDataTypeIds.filter(x => !newDataTypeIds.includes(x));

  // Get all assets to check componentType
  const allAssets = await getAssets();
  const currentAsset = allAssets.find(a => String(a.assetID) === String(assetId));
  const isSupplier = currentAsset?.componentType === 'Supplier';

  // For each added data type, add this asset to its asset list (if modeled)
  for (const dataTypeId of added) {
    console.log('[syncDataTypeAssets] Adding asset to data type:', { assetId, dataTypeId, isSupplier });
    const dataType = await getDataTypeById(Number(dataTypeId));
    console.log('[syncDataTypeAssets] Found data type:', dataType);
    if (dataType) {
      const dataTypeAny = dataType as any;
      let assets = Array.isArray(dataTypeAny.assets) ? [...dataTypeAny.assets] : [];
      let suppliers = Array.isArray(dataTypeAny.suppliers) ? [...dataTypeAny.suppliers] : [];
      
      if (isSupplier) {
        // Add to suppliers array only
        if (!suppliers.includes(assetId)) {
          suppliers.push(assetId);
          await updateDataType(Number(dataTypeId), { suppliers } as any);
        }
      } else {
        // Add to assets array only
        if (!assets.includes(assetId)) {
          assets.push(assetId);
          await updateDataType(Number(dataTypeId), { assets } as any);
        }
      }
    }
  }
  // For each removed data type, remove this asset from its asset list
  for (const dataTypeId of removed) {
    console.log('[syncDataTypeAssets] Removing asset from data type:', { assetId, dataTypeId, isSupplier });
    const dataType = await getDataTypeById(Number(dataTypeId));
    console.log('[syncDataTypeAssets] Found data type:', dataType);
    if (dataType) {
      const dataTypeAny = dataType as any;
      let assets = Array.isArray(dataTypeAny.assets) ? [...dataTypeAny.assets] : [];
      let suppliers = Array.isArray(dataTypeAny.suppliers) ? [...dataTypeAny.suppliers] : [];
      
      console.log('[syncDataTypeAssets][REMOVE] Initial arrays:', { assets, suppliers, assetId, typeof: typeof assetId });
      
      if (isSupplier) {
        // Remove from suppliers array only
        const beforeLength = suppliers.length;
        const filteredSuppliers = suppliers.filter(id => {
          const idStr = String(id);
          const assetIdStr = String(assetId);
          const keep = idStr !== assetIdStr;
          console.log('[syncDataTypeAssets][REMOVE] Comparing suppliers (as strings):', {id, idStr, assetId, assetIdStr, keep});
          return keep;
        });
        console.log('[syncDataTypeAssets][REMOVE] Filtered suppliers:', filteredSuppliers, 'Removed:', beforeLength - filteredSuppliers.length);
        if (suppliers.length !== filteredSuppliers.length) {
          const updateResult = await updateDataType(Number(dataTypeId), { suppliers: filteredSuppliers } as any);
          console.log('[syncDataTypeAssets][REMOVE] updateDataType result:', updateResult);
        }
      } else {
        // Remove from assets array only
        const beforeLength = assets.length;
        const filteredAssets = assets.filter(id => {
          const idStr = String(id);
          const assetIdStr = String(assetId);
          const keep = idStr !== assetIdStr;
          console.log('[syncDataTypeAssets][REMOVE] Comparing assets (as strings):', {id, idStr, assetId, assetIdStr, keep});
          return keep;
        });
        console.log('[syncDataTypeAssets][REMOVE] Filtered assets:', filteredAssets, 'Removed:', beforeLength - filteredAssets.length);
        if (assets.length !== filteredAssets.length) {
          const updateResult = await updateDataType(Number(dataTypeId), { assets: filteredAssets } as any);
          console.log('[syncDataTypeAssets][REMOVE] updateDataType result:', updateResult);
        }
      }
    }
  }
}
